import React, { useState, useEffect } from 'react';
import './Center.css';
import { areas } from './areas';
import axios from 'axios';
import Autocomplete from "react-google-autocomplete";
import Swal from 'sweetalert2';
import locationIcon from '../Image/location.png'; // Using your map pin icon
import { initFacebookPixel } from '../facebookPixel';

const Center = () => {
  const [centers, setCenters] = useState([]);
  const [currentImageIndices, setCurrentImageIndices] = useState({});
  const [error, setError] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const apiKey = process.env.REACT_APP_API_KEY;

  // --- Logic: Haversine Distance Formula ---
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  };

  // --- Logic: Dynamic Sorting ---
  const handleLocationSelection = (lat, lng) => {
    if (!centers.length) return;
    const sorted = centers.map(center => ({
      ...center,
      distance: calculateDistance(lat, lng, center.latitude, center.longitude)
    })).sort((a, b) => a.distance - b.distance);
    setCenters(sorted);
  };

  // --- Logic: Reverse Geocoding ---
  const getLocationName = async (latitude, longitude) => {
    try {
      const response = await axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
      );
      if (response.data.results && response.data.results.length > 0) {
        setSearchValue(response.data.results[0].formatted_address);
        handleLocationSelection(latitude, longitude);
      }
    } catch (err) {
      Swal.fire('Error', 'Failed to fetch address.', 'error');
    }
  };

  // --- Logic: GPS Trigger ---
  const updateLocation = () => {
    if (!navigator.geolocation) {
      Swal.fire('Error', 'Geolocation not supported.', 'error');
      return;
    }
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        getLocationName(latitude, longitude);
        setLocationLoading(false);
      },
      () => {
        setLocationLoading(false);
        Swal.fire('Location Error', 'Please enable location permissions.', 'error');
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  // --- Logic: Data Fetching (Badminton Category) ---
  const fetchSportsCenters = async () => {
    try {
      const response = await fetch(process.env.REACT_APP_API_URL_PURCHASE, {
        headers: { 'x-api-key': apiKey }
      });
      const data = await response.json();
      const defaultImages = [
        "https://machaxiprod.blob.core.windows.net/frontend-machaxi/learn-images/learn2.webp",
        "https://machaxiprod.blob.core.windows.net/dribble-images/1727034283075_MSC%20-%2021.webp"
      ];

      const centersData = data.sportsToAcademiesMap.Badminton.filter(center => center.id !== 115 && center.id !== 81 && center.id !== 86).map(center => {
        const academyId = center?.id;
        const areaCenter = areas.flatMap(area => area.centers).find(ac => ac.id === academyId);
        return {
          id: academyId,
          name: center?.name || 'Unknown Center',
          address: center?.address || '',
          latitude: parseFloat(center.latitude),
          longitude: parseFloat(center.longitude),
          sports: (data.academiesToSportsMap[academyId] || []).map(s => s.sport_name),
          images: data.academyImages?.[academyId] || defaultImages,
          locationUrl: areaCenter ? areaCenter.location : '#',
          distance: null
        };
      });

      setCenters(centersData);
      const initialIndices = {};
      centersData.forEach(c => initialIndices[c.id] = 0);
      setCurrentImageIndices(initialIndices);
    } catch (err) {
      setError('Failed to load centers.');
    }
  };

  useEffect(() => {
    initFacebookPixel();
    fetchSportsCenters();
  }, []);

  // --- Logic: Image Carousel ---
  useEffect(() => {
    const intervals = centers.map(center =>
      setInterval(() => {
        setCurrentImageIndices(prev => ({
          ...prev,
          [center.id]: (prev[center.id] + 1) % center.images.length
        }));
      }, 3000)
    );
    return () => intervals.forEach(clearInterval);
  }, [centers]);

  return (
    <div className="enroll-trial-container">
      <h2 className="enroll-title">Centres Near Me</h2>

      {/* 1. Pill Search Section */}
      <div className="location-search-section">
        <label className="trial-time-label">Find a Centre Near You</label>
        <div className="pill-search-container">
          <Autocomplete
            apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
            className="pill-search-input"
            onPlaceSelected={(place) => {
              if (place.geometry) {
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();
                setSearchValue(place.formatted_address);
                handleLocationSelection(lat, lng);
              }
            }}
            options={{ types: ["geocode", "establishment"], componentRestrictions: { country: "in" } }}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Enter your locality or area..."
          />
          <button
            type="button"
            className={`pill-geo-button ${locationLoading ? 'loading' : ''}`}
            onClick={updateLocation}
          >
            <img src={locationIcon} alt="GPS" className="pill-geo-icon" />
          </button>
        </div>
      </div>

      {/* 2. Results Section */}
      <div className="centers-list-container">
        {error ? (
          <p className="error-message">{error}</p>
        ) : locationLoading ? (
          <div className="fetching-loader">
            <p>Fetching location...</p>
          </div>
        ) : (
          <div className="centers-details-row">
            {centers.map((center) => (
              <div key={center.id} className="mini-center-card">
                <div className="image-container-mini">
                  <div className="sports-overlay-bar-mini">
                    {center.sports.map((sport, idx) => (
                      <div key={idx} className="sport-tag-container">
                        <span className="sport-name-label-mini">{sport}</span>
                      </div>
                    ))}
                  </div>

                  <img
                    src={center.images[currentImageIndices[center.id]]}
                    alt={center.name}
                    className="center-image-mini"
                  />

                  {center.distance && (
                    <div className="distance-badge-mini">
                      {center.distance.toFixed(1)} km away
                    </div>
                  )}
                </div>

                <div className="info-container-mini">
                  <div className="text-content">
                    <h3 className="center-name-mini">{center.name}</h3>
                    <p className="center-address-mini">{center.address}</p>
                  </div>

                  <a
                    href={center.locationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="view-on-map-text-link"
                  >
                    View on Map
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Center;
